import { useCallback, useEffect, useRef, useState } from "react";
import { searchSubscribers } from "../lib/client";
import type { SubscriberSummary } from "../lib/types";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Relay Global IDs (base64("Subscriber:<uuid>")) are opaque to us client-side
// — ps-main's `nameOrId` filter is what actually decodes them — but they look
// nothing like a name (base64 alphabet, no spaces, long), so this is a safe
// heuristic for "treat this like a pasted id, not a search-as-you-type term."
const GLOBAL_ID_RE = /^[A-Za-z0-9+/]{16,}={0,2}$/;

function looksLikeId(value: string): boolean {
  return UUID_RE.test(value) || GLOBAL_ID_RE.test(value);
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

interface SubscriberPickerProps {
  /** Display text: the resolved subscriber's name, or a raw UUID if typed directly. */
  value: string;
  /**
   * The text of the label, and the base of the input id. A page that shows 2
   * pickers must give a different value to each one. A DOM id must be unique,
   * or the label of one picker moves the focus to the input of the other.
   */
  name?: string;
  /** Called with ("", "") to invalidate the current selection, or (id, name) once resolved. */
  onResolve: (id: string, name: string) => void;
}

/**
 * Searchable subscriber autocomplete. Replaces hand-typing a subscriber UUID:
 * type a name to search platform-service-main via GraphQL, paste a UUID
 * directly to resolve it immediately, or paste a subscriber's Relay Global ID
 * (as copied from some other GraphQL tool) — ps-main's own `nameOrId` filter
 * decodes that server-side, and an unambiguous single match auto-resolves the
 * same way a UUID does.
 */
export function SubscriberPicker({
  value,
  name = "Subscriber",
  onResolve,
}: SubscriberPickerProps) {
  const inputId = `subscriber-search-${name.toLowerCase().replace(/\W+/g, "-")}`;
  const [text, setText] = useState(value);
  const [results, setResults] = useState<SubscriberSummary[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const focusedRef = useRef(false);
  // Skip searching on first mount when we already have a persisted selection
  // to display — only real edits after that should trigger a fresh search.
  const isFirstRun = useRef(true);
  // Set right before a programmatic setText(sub.name) in select() below, so
  // that text change doesn't immediately re-search and flash the dropdown
  // back open right after the user just picked something.
  const suppressNextSearchRef = useRef(false);
  // Kept current via the effect below so the search effect doesn't need
  // onResolve in its own dependency list (its identity changes per render).
  const onResolveRef = useRef(onResolve);
  // Guards against the focus-triggered "browse" fetch and a later typed
  // search landing out of order (whichever fetch was started most recently
  // wins; a slower, stale one is dropped instead of clobbering fresh results).
  const requestIdRef = useRef(0);

  useEffect(() => {
    onResolveRef.current = onResolve;
  }, [onResolve]);

  useEffect(() => {
    setText(value);
  }, [value]);

  // Uses onResolveRef (not the `onResolve` prop directly) so it stays correct
  // even when invoked from runSearch below, which is memoized once via
  // useCallback([]) and would otherwise close over a stale `onResolve`.
  const select = useCallback((sub: SubscriberSummary) => {
    suppressNextSearchRef.current = true;
    setText(sub.name);
    onResolveRef.current(sub.id, sub.name);
    setOpen(false);
    setResults([]);
  }, []);

  /** Fetch results for `search` (possibly "" for an unfiltered browse list). */
  const runSearch = useCallback(
    async (search: string) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const matches = await searchSubscribers(search);
        if (requestIdRef.current !== requestId) return; // superseded
        // A pasted id (UUID or Relay Global ID) that resolves to exactly one
        // subscriber is unambiguous — skip the extra click and resolve it
        // immediately, same as pasting a raw UUID does.
        const [onlyMatch] = matches;
        if (onlyMatch && matches.length === 1 && looksLikeId(search)) {
          select(onlyMatch);
          return;
        }
        setResults(matches);
        setOpen(matches.length > 0 && focusedRef.current);
        setHighlighted(0);
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setError(err instanceof Error ? err.message : "Search failed.");
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    },
    [select],
  );

  useEffect(() => {
    const skipSearch = isFirstRun.current || suppressNextSearchRef.current;
    isFirstRun.current = false;
    suppressNextSearchRef.current = false;

    const trimmed = text.trim();

    if (UUID_RE.test(trimmed)) {
      onResolveRef.current(trimmed, "");
      setResults([]);
      setOpen(false);
      return;
    }

    // Clearing the box invalidates the previous selection, but an empty
    // query is still worth searching (see below) — it's how the "browse an
    // unfiltered list" behavior kicks in when the box is cleared while
    // focused, not just on the very first focus.
    if (!trimmed) {
      onResolveRef.current("", "");
    }

    if (trimmed.length > 0 && trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setOpen(false);
      return;
    }

    if (skipSearch) {
      setResults([]);
      setOpen(false);
      return;
    }

    const handle = setTimeout(() => {
      runSearch(trimmed);
    }, DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [text, runSearch]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleFocus() {
    focusedRef.current = true;
    if (results.length > 0) {
      setOpen(true);
      return;
    }
    // Nothing typed and nothing loaded yet — browse an unfiltered (sorted)
    // page so a user who doesn't remember the exact name has somewhere to
    // start. Only fires here (not from the [text] effect) because focusing
    // an already-empty box doesn't change `text`, so that effect wouldn't
    // otherwise re-run.
    if (!text.trim()) {
      runSearch("");
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => (h + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => (h - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const sub = results[highlighted];
      if (sub) select(sub);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="subscriber-picker" ref={containerRef}>
      <label htmlFor={inputId}>{name}</label>
      <input
        id={inputId}
        className="subscriber-input"
        placeholder="Click to browse, search by name, or paste a subscriber UUID"
        value={text}
        spellCheck={false}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={() => {
          focusedRef.current = false;
        }}
      />
      {loading && <span className="hint">Searching…</span>}
      {error && <span className="hint error">{error}</span>}
      {open && results.length > 0 && (
        <ul className="subscriber-results">
          {results.map((r, i) => (
            <li
              key={r.id}
              className={i === highlighted ? "active" : ""}
              onMouseDown={(e) => {
                // Fires before the input's blur, so the click still lands.
                e.preventDefault();
                select(r);
              }}
              onMouseEnter={() => setHighlighted(i)}
            >
              <span className="subscriber-name">{r.name}</span>
              {r.ein && <span className="subscriber-ein">{r.ein}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
